import {
  GlobalSearchAnalyticsSession,
  type SearchResultMetadata,
} from 'analytics/globalSearchAnalytics';
import type {SearchRankingVariant} from 'analytics/searchAnalytics';
import {
  GlobalSearchResults,
  INITIAL_RESULTS,
  isEmployee,
  isKnowledgeResult,
  isNewsPost,
  isPlanning,
  isTeamSearchResult,
  isWorkstream,
  isWorkstreamNavigation,
  MAX_RESULTS,
} from 'hooks/useEmployee';
import {useCallback, useEffect, useRef} from 'react';

type GlobalSearchFilter = 'all' | 'people' | 'teams' | 'news' | 'workstreams' | 'knowledge';
type SearchResultWithIndex = Exclude<GlobalSearchResults, undefined> & {idx: number};

export type GlobalSearchSelection = {
  route: string;
  path: string;
  index?: number;
};

const analyticsResultForItem = (item: SearchResultWithIndex) => {
  const metadata = (item as {search_metadata?: SearchResultMetadata}).search_metadata;
  if (isKnowledgeResult(item)) {
    return {
      entityType: 'knowledge' as const,
      localIdentity: `knowledge:${item.id}`,
      rank: item.idx,
      metadata,
    };
  }
  if (isEmployee(item)) {
    return {
      entityType: 'person' as const,
      localIdentity: `people:${item.ldap}`,
      rank: item.idx,
      metadata,
    };
  }
  if (isTeamSearchResult(item)) {
    return {
      entityType: 'team' as const,
      localIdentity: `teams:${item.slug}`,
      rank: item.idx,
      metadata,
    };
  }
  if (isWorkstreamNavigation(item)) {
    return {
      entityType: 'workstream_scope' as const,
      localIdentity: `workstream_scope:${item.id}`,
      rank: item.idx,
      metadata,
    };
  }
  if (isWorkstream(item)) {
    return {
      entityType: 'workstream' as const,
      localIdentity: `workstream:${item.id}`,
      rank: item.idx,
      metadata,
    };
  }
  if (isNewsPost(item)) {
    return {
      entityType: 'news' as const,
      localIdentity: `news:${item.id}`,
      rank: item.idx,
      metadata,
    };
  }
  if (isPlanning(item)) {
    return {
      entityType: 'planning' as const,
      localIdentity: `planning:${item.id}`,
      rank: item.idx,
      metadata,
    };
  }
  return undefined;
};

const analyticsResultForSelection = (
  {route, path, index = 0}: GlobalSearchSelection,
  results: SearchResultWithIndex[]
) => {
  const entityTypeByRoute = {
    people: 'person',
    teams: 'team',
    workstream: 'workstream',
    workstream_scope: 'workstream_scope',
    news: 'news',
    planning: 'planning',
    knowledge: 'knowledge',
  } as const;
  const entityType = entityTypeByRoute[route as keyof typeof entityTypeByRoute];

  if (!entityType) return undefined;

  const localIdentity = `${route}:${path}`;
  const visibleResult = results
    .map(analyticsResultForItem)
    .find((result) => result?.localIdentity === localIdentity);
  return {
    entityType,
    localIdentity,
    rank: index,
    metadata: visibleResult?.metadata,
  };
};

const entryPointForSearch = (source: string | false) => {
  if (source === 'queryparam') return 'query_param';
  if (source === 'keyboard') return 'keyboard';
  if (source === 'home') return 'home';
  return 'nav';
};

export const useGlobalSearchAnalytics = ({
  entryPoint,
  query,
  hasLiveQuery,
  activeFilter,
  results,
  resultsPending,
  showMore,
  rankingMode,
  askRowPromoted,
}: {
  entryPoint: string | false;
  query: string;
  hasLiveQuery: boolean;
  activeFilter: GlobalSearchFilter;
  results: SearchResultWithIndex[];
  resultsPending: boolean;
  showMore: boolean;
  rankingMode: SearchRankingVariant;
  askRowPromoted: boolean;
}) => {
  const sessionRef = useRef<GlobalSearchAnalyticsSession>();
  const requestStartedAtRef = useRef(performance.now());
  const lastResultsSignatureRef = useRef<string>();
  const previousFilterRef = useRef<GlobalSearchFilter>('all');
  const lastActiveFilterRef = useRef<GlobalSearchFilter>('all');
  const currentRequestRef = useRef<{id: string; filter: GlobalSearchFilter}>();
  const selectionMethodRef = useRef<'click' | 'keyboard'>('click');
  const sessionCleanupTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (sessionCleanupTimerRef.current !== undefined) {
      clearTimeout(sessionCleanupTimerRef.current);
      sessionCleanupTimerRef.current = undefined;
    }

    if (!sessionRef.current) {
      sessionRef.current = new GlobalSearchAnalyticsSession(
        entryPointForSearch(entryPoint),
        rankingMode
      );
    }

    return () => {
      // StrictMode replays effects immediately in development. Deferring cleanup
      // lets its second setup retain this session, while a real unmount still
      // emits exactly one terminal event.
      sessionCleanupTimerRef.current = setTimeout(() => {
        sessionRef.current?.end({activeFilter: lastActiveFilterRef.current});
        sessionRef.current = undefined;
        sessionCleanupTimerRef.current = undefined;
      }, 0);
    };
    // The entry point is intentionally captured only when the search session opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    requestStartedAtRef.current = performance.now();
  }, [activeFilter, query, showMore]);

  useEffect(() => {
    if (!hasLiveQuery) {
      currentRequestRef.current = undefined;
    }
  }, [hasLiveQuery]);

  useEffect(() => {
    if (!hasLiveQuery || resultsPending) return;

    // The raw query only deduplicates browser-side request handling.
    // It is never emitted as an analytics event property.
    const signature = `${query}\u0000${activeFilter}\u0000${showMore}`;
    if (lastResultsSignatureRef.current === signature) return;
    lastResultsSignatureRef.current = signature;

    const visibleResults = results
      .slice(0, showMore ? MAX_RESULTS : INITIAL_RESULTS)
      .map(analyticsResultForItem)
      .filter((result): result is NonNullable<typeof result> => Boolean(result));
    const request = sessionRef.current?.resultsViewed({
      query,
      activeFilter,
      previousFilter: previousFilterRef.current,
      resultCount: results.length,
      shownTopK: visibleResults.length,
      elapsedMs: performance.now() - requestStartedAtRef.current,
      rankingVariant: rankingMode,
      askRowPromoted,
    });
    previousFilterRef.current = activeFilter;
    if (!request) return;

    currentRequestRef.current = {id: request.requestId, filter: activeFilter};
    sessionRef.current?.resultsImpressed({
      requestId: request.requestId,
      activeFilter,
      results: visibleResults,
    });
  }, [
    activeFilter,
    askRowPromoted,
    hasLiveQuery,
    query,
    rankingMode,
    results,
    resultsPending,
    showMore,
  ]);

  const trackFilterChange = useCallback(
    (nextFilter: GlobalSearchFilter) => {
      if (nextFilter === activeFilter) return;
      lastActiveFilterRef.current = nextFilter;
      sessionRef.current?.markFilterChanged();
    },
    [activeFilter]
  );

  const trackFilterReset = useCallback(() => {
    lastActiveFilterRef.current = 'all';
  }, []);

  const markKeyboardSelection = useCallback(() => {
    selectionMethodRef.current = 'keyboard';
  }, []);

  const trackResultSelection = useCallback(
    (selection: GlobalSearchSelection) => {
      const selectedResult = analyticsResultForSelection(selection, results);
      if (selectedResult && currentRequestRef.current?.filter === activeFilter && !resultsPending) {
        sessionRef.current?.resultSelected({
          requestId: currentRequestRef.current.id,
          activeFilter,
          result: selectedResult,
          selectionMethod: selectionMethodRef.current,
        });
      }
      selectionMethodRef.current = 'click';
    },
    [activeFilter, results, resultsPending]
  );

  const trackShowMore = useCallback(() => {
    sessionRef.current?.showMore();
  }, []);

  return {
    markKeyboardSelection,
    trackFilterChange,
    trackFilterReset,
    trackResultSelection,
    trackShowMore,
  };
};
